import { useState } from "react";
import React from 'react';

function calculateCheck(base12) {
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    const n = Number(base12[i]);
    sum += n * (i % 2 === 0 ? 1 : 3);
  }
  return (10 - (sum % 10)) % 10;
}

function fixChecksum(code13) {
  const digits = String(code13).replace(/\D/g, "");
  if (digits.length !== 13) return "Invalid input (must be 13 digits)";
  const base12 = digits.slice(0, 12);
  return base12 + calculateCheck(base12);
}

function generateUniqueEAN13() {
  const prefix = "98"; // internal range (change if needed)

  const timePart = Date.now().toString().slice(-6); // 6 digits
  const randomPart = Math.floor(Math.random() * 10000)
    .toString()
    .padStart(4, "0");

  const base12 = (prefix + timePart + randomPart).slice(0, 12);
  return base12 + calculateCheck(base12);
}

// --- Barcode SVG renderer ---
function BarcodeDisplay({ code }) {
  if (!code || code.length !== 13) return null;

  // EAN-13 encoding tables
  const L = ["0001101","0011001","0010011","0111101","0100011","0110001","0101111","0111011","0110111","0001011"];
  const G = ["0100111","0110011","0011011","0100001","0011101","0111001","0000101","0010001","0001001","0010111"];
  const R = ["1110010","1100110","1101100","1000010","1011100","1001110","1010000","1000100","1001000","1110100"];

  const firstDigit = parseInt(code[0]);
  const parityPatterns = [
    "LLLLLL","LLGLGG","LLGGLG","LLGGGL","LGLLGG","LGGLLG","LGGGLL","LGLGLG","LGLGGL","LGGLGL"
  ];
  const parity = parityPatterns[firstDigit];

  let bars = [];
  // Left quiet zone
  bars.push({ width: 9, dark: false });
  // Start guard
  bars.push({ width: 1, dark: true });
  bars.push({ width: 1, dark: false });
  bars.push({ width: 1, dark: true });

  // Left 6 digits (2-7)
  for (let i = 1; i <= 6; i++) {
    const d = parseInt(code[i]);
    const enc = parity[i - 1] === "L" ? L[d] : G[d];
    for (let b = 0; b < 7; b++) {
      bars.push({ width: 1, dark: enc[b] === "1" });
    }
  }

  // Center guard
  bars.push({ width: 1, dark: false });
  bars.push({ width: 1, dark: true });
  bars.push({ width: 1, dark: false });
  bars.push({ width: 1, dark: true });
  bars.push({ width: 1, dark: false });

  // Right 6 digits (7-12)
  for (let i = 7; i <= 12; i++) {
    const d = parseInt(code[i]);
    const enc = R[d];
    for (let b = 0; b < 7; b++) {
      bars.push({ width: 1, dark: enc[b] === "1" });
    }
  }

  // End guard
  bars.push({ width: 1, dark: true });
  bars.push({ width: 1, dark: false });
  bars.push({ width: 1, dark: true });
  // Right quiet zone
  bars.push({ width: 9, dark: false });

  let x = 0;
  const svgBars = bars.map((bar, i) => {
    const el = bar.dark ? (
      <rect key={i} x={x} y={0} width={bar.width} height={60} fill="#1a1a2e" />
    ) : null;
    x += bar.width;
    return el;
  });
  const totalWidth = x;

  return (
    <svg viewBox={`0 0 ${totalWidth} 70`} style={{ width: "100%", maxWidth: 280 }}>
      <rect x={0} y={0} width={totalWidth} height={70} fill="white" />
      {svgBars}
      <text x={totalWidth / 2} y={68} textAnchor="middle" fontSize="7" fontFamily="'Courier New', monospace" fill="#1a1a2e" letterSpacing="1">
        {code.split("").join(" ")}
      </text>
    </svg>
  );
}

// --- Main Component ---
export default function App() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState(null); // 'fix' | 'gen'

  const handleFind = () => {
    setMode("fix");
    setOutput(fixChecksum(input));
    setCopied(false);
  };

  const handleUnique = () => {
    setMode("gen");
    setOutput(generateUniqueEAN13());
    setCopied(false);
  };

  const handleCopy = () => {
    if (output) {
      navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      <style>{`
   
        .Barcode-wrapper {
          width: 50%;
          padding: 16px;
        }

        .d-Barcode-card {
          background: #12122b;
          border: 1px solid #2a2a5a;
          border-radius: 20px;
          padding: 32px 28px;
          position: relative;
          overflow: hidden;
          box-shadow: 0 0 60px rgba(99, 102, 241, 0.12), 0 20px 40px rgba(0,0,0,0.4);
        }

        .d-Barcode-card::before {
          content: '';
          position: absolute;
          top: -60px; right: -60px;
          width: 180px; height: 180px;
          background: radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%);
          pointer-events: none;
        }

        .d-Barcode-card::after {
          content: '';
          position: absolute;
          bottom: -40px; left: -40px;
          width: 140px; height: 140px;
          background: radial-gradient(circle, rgba(236,72,153,0.12) 0%, transparent 70%);
          pointer-events: none;
        }

        .Barcode-Generate-header {
          margin-bottom: 28px;
        }

        .Barcode-Generate-badge {
          display: inline-block;
          background: rgba(99,102,241,0.15);
          border: 1px solid rgba(99,102,241,0.3);
          color: #818cf8;
          font-size: 10px;
          letter-spacing: 2px;
          text-transform: uppercase;
          padding: 4px 10px;
          border-radius: 4px;
          margin-bottom: 10px;
        }

        .Barcode-Generate-title {
          font-family: 'Syne', sans-serif;
          font-weight: 800;
          font-size: 26px;
          color: #ffffff;
          line-height: 1.1;
          letter-spacing: -0.5px;
        }

        .Barcode-Generate-title span {
          color: #818cf8;
        }

        .Barcode-Generate-subtitle {
          color: #f1f5ffff;
          font-size: 11px;
          margin-top: 6px;
          letter-spacing: 0.5px;
        }

        .Bar-Gen-input-section {
          margin-bottom: 16px;
        }

        .Bar-Gen-input-lab {
          color: #e7e7e7ff;
          font-size: 10px;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          margin-bottom: 8px;
          display: block;
        }

        .input-wrap {
          position: relative;
        }

        .input-wrap input {
          width: 100%;
          background: #0d0d1a;
          border: 1px solid #2a2a5a;
          border-radius: 12px;
          padding: 14px 16px;
          color: #e2e8f0;
          font-family: 'Space Mono', monospace;
          font-size: 15px;
          letter-spacing: 2px;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .input-wrap input:focus {
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99,102,241,0.15);
        }

        .input-wrap input::placeholder {
          color: #374151;
          letter-spacing: 1px;
          font-size: 12px;
        }

        .char-count {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 10px;
          color: ${input.replace(/\D/g, "").length === 13 ? "#34d399" : "#4b5563"};
          transition: color 0.2s;
        }

        .d-Bar-buttons {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 24px;
        }

        .d-bar-flex {
          padding: 13px 16px;
          border-radius: 12px;
          border: none;
          font-family: 'Space Mono', monospace;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          cursor: pointer;
          transition: all 0.2s;
          position: relative;
          overflow: hidden;
        }

        .bar-gen-btn-primary {
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: white;
          box-shadow: 0 4px 15px rgba(99,102,241,0.3);
        }

        .bar-gen-btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(99,102,241,0.45);
        }

        .bar-gen-btn-primary:active { transform: translateY(0); }

        .d-Bar-btn-secondary {
          background: rgba(236,72,153,0.1);
          color: #f472b6;
          border: 1px solid rgba(236,72,153,0.25);
        }

        .d-Bar-btn-secondary:hover {
          border-color: rgba(236,72,153,0.5);
          transform: translateY(-2px);
        }

        .d-Bar-btn-secondary:active { transform: translateY(0); }

        .result-d-Barcode-card {
          background: #0d0d1a;
          border: 1px solid #1e1e4a;
          border-radius: 14px;
          padding: 20px;
          animation: slideUp 0.3s ease;
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .result-label {
          font-size: 9px;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: #4b5563;
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .result-label::before {
          content: '';
          display: inline-block;
          width: 6px; height: 6px;
          border-radius: 50%;
          background: #34d399;
          animation: pulse 1.5s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }

        .result-code {
          font-family: 'Space Mono', monospace;
          font-size: 22px;
          font-weight: 700;
          color: #e2e8f0;
          letter-spacing: 3px;
          margin-bottom: 4px;
          word-break: break-all;
        }

        .result-code .digit-group {
          color: #818cf8;
        }

        .result-meta {
          font-size: 10px;
          color: #fcfcfcff;
          margin-bottom: 16px;
          letter-spacing: 0.5px;
        }

        .barcode-wrap {
          background: white;
          border-radius: 8px;
          padding: 12px 16px 8px;
          margin-bottom: 14px;
        }

        .d-BarCode-copy-btn {
          width: 100%;
          padding: 10px;
          background: rgba(52,211,153,0.08);
          border: 1px solid rgba(52,211,153,0.2);
          border-radius: 8px;
          color: #34d399;
          font-family: 'Space Mono', monospace;
          font-size: 10px;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          cursor: pointer;
          transition: all 0.2s;
        }

        .d-BarCode-copy-btn:hover {
          background: rgba(52,211,153,0.14);
          border-color: rgba(52,211,153,0.4);
        }

        .d-BarCode-copy-btn.copied {
          color: #6ee7b7;
          border-color: rgba(110,231,183,0.3);
        }

        .d-BarCode-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, #2a2a5a, transparent);
          margin: 22px 0;
        }

        .d-BarCode-footer {
          text-align: center;
          color: #374151;
          font-size: 9px;
          letter-spacing: 1px;
        }
      `}</style>

      <div className="Barcode-wrapper mx-auto">
        <div className="d-Barcode-card">
          <div className="Barcode-Generate-header">
            <div className="Barcode-Generate-badge">GS1 Standard</div>
            <h1 className="Barcode-Generate-title">EAN<span>-13</span><br />Barcode Tool</h1>
            <p className="Barcode-Generate-subtitle">Generate & validate barcodes in real time</p>
          </div>

          <div className="Bar-Gen-input-section">
            <label className="Bar-Gen-input-lab">Barcode Input</label>
            <div className="input-wrap">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value.replace(/\D/g, "").slice(0, 13))}
                placeholder="0000000000000"
                maxLength={13}
              />
              <span className="char-count">{input.replace(/\D/g, "").length}/13</span>
            </div>
          </div>

          <div className="d-Bar-buttons">
            <button className="d-bar-flex bar-gen-btn-primary" onClick={handleFind}>
              Fix / Validate
            </button>
            <button className="d-bar-flex d-Bar-btn-secondary" onClick={handleUnique}>
              Generate
            </button>
          </div>

          {output && (
            <div className="result-d-Barcode-card">
              <div className="result-label">
                {mode === "gen" ? "Generated Code" : "Fixed Checksum"}
              </div>

              <div className="result-code">
                <span>{output}</span>
              </div>

              <div className="result-meta">
                {mode === "gen" ? "Randomly generated · Valid EAN-13" : `Input → Checksum corrected to ${output.slice(-1)}`}
              </div>

              <div className="barcode-wrap">
                <BarcodeDisplay code={output} />
              </div>

              <button className="d-BarCode-copy-btn copied={copied}" onClick={handleCopy} style={{ color: copied ? "#6ee7b7" : "" }}>
                {copied ? "✓ Copied!" : "Copy to Clipboard"}
              </button>
            </div>
          )}

          <div className="d-BarCode-divider" />
          <div className="d-BarCode-footer">EAN-13 · ISO/IEC 15420 · GS1 Specification</div>
        </div>
      </div>
    </>
  );
}